import React from "react";
import { FileText, Bell, Ship } from "lucide-react";

/* ------------------ Live Freight Dashboard (full width, dark) ------------------ */
export const LiveFreightDashboard = ({ testIds: T }) => {
  const topStats = [
    { l: "Sync Status", v: "Live", meta: "Updated 2s ago", tone: "text-emerald-300" },
    { l: "Active Shipments", v: "7", meta: "Across 6 carriers", tone: "text-white" },
    { l: "ETA Risk", v: "Low", meta: "2 issues monitored", tone: "text-sky-300" },
    { l: "Coordination Time", v: "1.4d", meta: "38% faster", tone: "text-[#FFB08F]" },
  ];

  const kpis = [
    { l: "Open Bookings", v: "12", s: "+3 this week", tone: "text-white" },
    { l: "Containers In Transit", v: "84", s: "Across 6 carriers", tone: "text-white" },
    { l: "Avg. Coordination Time", v: "1.4 days", s: "38% faster", tone: "text-emerald-300" },
    { l: "Active Alerts", v: "2", s: "ETA shift detected", tone: "text-amber-300" },
  ];

  const shipments = [
    { id: "SH-2481", c: "MSC", r: "Mersin -> Mombasa", m: "At Sea", e: "12 Jun", h: 98 },
    { id: "SH-2479", c: "Maersk", r: "Ambarli -> Durban", m: "Booking Confirmed", e: "27 Jun", h: 94 },
    { id: "SH-2475", c: "CMA CGM", r: "Aliaga -> Lagos", m: "Container Loaded", e: "14 Jun", h: 91 },
    { id: "SH-2470", c: "Hapag-Lloyd", r: "Iskenderun -> Dar es Salaam", m: "Vessel Departed", e: "21 Jun", h: 88 },
    { id: "SH-2466", c: "ONE", r: "Mersin -> Tema", m: "Documents Pending", e: "30 Jun", h: 73, warn: true },
  ];

  const timeline = [
    { l: "PO Approved", d: "28 May", ok: true, tag: "done" },
    { l: "Carrier Assigned", d: "29 May", ok: true, tag: "done" },
    { l: "Booking Confirmed", d: "01 Jun", ok: true, tag: "done" },
    { l: "Container Loaded", d: "03 Jun", ok: true, tag: "done" },
    { l: "Vessel Departed", d: "03 Jun", ok: true, tag: "done" },
    { l: "At Sea", d: "Now live", ok: true, active: true, tag: "active" },
    { l: "Destination Port", d: "12 Jun ETA", ok: false, tag: "next" },
    { l: "Delivered", d: "Pending", ok: false, tag: "pending" },
  ];

  return (
    <div
      data-testid={T.liveDashboardSection + "-mockup"}
      className="relative overflow-hidden rounded-[2rem] bg-[#0D1330] text-white shadow-[0_50px_120px_-40px_rgba(0,0,0,0.82)] ring-1 ring-white/5"
    >
      <div className="pointer-events-none absolute inset-0 fiq-grid-bg-dark opacity-40" />
      <div className="pointer-events-none absolute -right-20 top-0 h-[320px] w-[320px] rounded-full bg-[#FF6B35]/12 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-[-6rem] left-[-5rem] h-[240px] w-[240px] rounded-full bg-sky-400/8 blur-[110px]" />

      <div className="relative p-4 sm:p-5 lg:p-6">
        <div className="rounded-[1.75rem] bg-[#111936]/90 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-white/6 sm:p-6 lg:p-7">
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.18fr)_minmax(420px,0.82fr)] xl:items-start">
            <div>
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.05] ring-1 ring-white/6">
                  <Ship className="h-5 w-5 text-[#FF6B35]" />
                </div>
                <div>
                  <div className="fiq-overline text-white/38">DeMaxtore Workspace</div>
                  <div className="text-sm font-semibold text-white/88">Acme Imports · Q2 Freight Control Tower</div>
                </div>
              </div>
              <h3 className="max-w-[18ch] font-display text-[1.85rem] font-bold leading-[1.02] tracking-tight text-white sm:text-[2.2rem]">
                Boardroom-grade visibility for every live shipment.
              </h3>
              <p className="mt-3 max-w-[62ch] text-sm leading-7 text-white/62 sm:text-base">
                Booking, container, milestone, ETA and exception data are synchronized into one
                freight surface so operators can see what matters without jumping between tools.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-2">
              {topStats.map((item) => (
                <div
                  key={item.l}
                  className="rounded-2xl bg-white/[0.04] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-white/6"
                >
                  <div className="fiq-overline text-white/30">{item.l}</div>
                  <div className={`mt-2 font-display text-2xl font-bold tracking-tight ${item.tone}`}>
                    {item.v}
                  </div>
                  <div className="mt-1 text-[11px] font-mono text-white/45">{item.meta}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {kpis.map((k, i) => (
              <div
                key={i}
                data-testid={T.liveDashboardPanel(`kpi-${i}`)}
                className="rounded-2xl bg-white/[0.04] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-white/6 sm:px-5"
              >
                <div className="fiq-overline text-white/30">{k.l}</div>
                <div className={`mt-3 font-display text-4xl font-bold tracking-tight ${k.tone}`}>{k.v}</div>
                <div className="mt-1.5 text-[11px] font-mono text-white/45">{k.s}</div>
              </div>
            ))}
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.82fr)]">
            <div className="rounded-[1.5rem] bg-[#151D3D] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-white/6 sm:p-5">
              <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="fiq-overline mb-1 text-white/30">Active Coordination</div>
                  <h4 className="font-display text-xl font-semibold text-white">Shipments in Workspace</h4>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {["All", "At Sea", "Booking", "Documents"].map((p, i) => (
                    <span
                      key={i}
                      className={`rounded-full px-3 py-1 text-[11px] ${
                        i === 0
                          ? "bg-[#FF6B35]/12 text-[#FFB08F] ring-1 ring-[#FF6B35]/20"
                          : "bg-white/[0.04] text-white/58 ring-1 ring-white/6"
                      }`}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>

              <div
                data-testid={T.liveDashboardPanel("shipments-table")}
                className="overflow-hidden rounded-[1.25rem] bg-[#1A2246] ring-1 ring-white/6"
              >
                <div className="overflow-x-auto">
                  <div className="min-w-[760px]">
                    <div className="grid grid-cols-12 bg-white/[0.025] px-4 py-3 text-[10px] font-mono uppercase tracking-[0.18em] text-white/32">
                      <div className="col-span-2">Shipment</div>
                      <div className="col-span-2">Carrier</div>
                      <div className="col-span-3">Route</div>
                      <div className="col-span-2">Milestone</div>
                      <div className="col-span-2">ETA</div>
                      <div className="col-span-1">Health</div>
                    </div>
                    {shipments.map((row, i) => (
                      <div
                        key={i}
                        className="grid grid-cols-12 items-center px-4 py-3.5 text-xs transition-colors odd:bg-transparent even:bg-white/[0.015] hover:bg-white/[0.03]"
                      >
                        <div className="col-span-2 font-mono font-semibold text-white">{row.id}</div>
                        <div className="col-span-2 text-white/70">{row.c}</div>
                        <div className="col-span-3 text-white/70">{row.r}</div>
                        <div className="col-span-2">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ring-1 ${
                              row.warn
                                ? "bg-amber-500/10 text-amber-300 ring-amber-500/18"
                                : "bg-emerald-500/10 text-emerald-300 ring-emerald-500/18"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full fiq-pulse-dot ${
                                row.warn ? "bg-amber-400" : "bg-emerald-400"
                              }`}
                            />
                            {row.m}
                          </span>
                        </div>
                        <div className="col-span-2 font-mono text-white/80">{row.e}</div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <div className="h-1.5 w-10 overflow-hidden rounded-full bg-white/10">
                              <div
                                className={`h-full ${
                                  row.h >= 90 ? "bg-emerald-400" : row.h >= 80 ? "bg-yellow-400" : "bg-amber-400"
                                }`}
                                style={{ width: `${row.h}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-mono text-white/60">{row.h}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-[1.25rem] bg-[#192142] p-4 ring-1 ring-white/6 sm:p-5">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <div className="fiq-overline mb-1 text-white/30">Operation Timeline</div>
                    <div className="text-base font-semibold text-white">Shipment SH-2481 progress</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[
                      { l: "Completed", v: "5 / 8", tone: "text-emerald-300" },
                      { l: "Current", v: "At Sea", tone: "text-[#FFB08F]" },
                      { l: "Next", v: "Port ETA", tone: "text-sky-300" },
                      { l: "Arrival", v: "12 Jun", tone: "text-white" },
                    ].map((item) => (
                      <div
                        key={item.l}
                        className="rounded-xl bg-white/[0.04] px-3 py-3 ring-1 ring-white/6"
                      >
                        <div className="fiq-overline text-white/28">{item.l}</div>
                        <div className={`mt-2 text-sm font-semibold ${item.tone}`}>{item.v}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4 h-2 overflow-hidden rounded-full bg-white/8">
                  <div className="h-full w-[68%] rounded-full bg-gradient-to-r from-emerald-400 via-[#FFB08F] to-[#FF6B35]" />
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
                  {timeline.map((s) => (
                    <div
                      key={s.l}
                      className={`relative flex min-h-[138px] flex-col rounded-2xl px-3 py-3 ring-1 ${
                        s.active
                          ? "bg-[#FF6B35]/10 ring-[#FF6B35]/18"
                          : s.ok
                            ? "bg-emerald-400/[0.05] ring-emerald-400/10"
                            : "bg-white/[0.025] ring-white/6"
                      }`}
                    >
                      <div className="relative flex h-8 items-center">
                        {s.active && <span className="absolute h-10 w-10 rounded-full bg-[#FF6B35]/14" />}
                        <span
                          className={`relative flex h-5 w-5 items-center justify-center rounded-full border ${
                            s.active
                              ? "border-[#FF8A5B] bg-[#FF6B35] shadow-[0_0_0_6px_rgba(255,107,53,0.15)]"
                              : s.ok
                                ? "border-emerald-300/70 bg-emerald-400"
                                : "border-white/15 bg-white/10"
                          }`}
                        >
                          {s.active ? (
                            <Ship className="h-2.5 w-2.5 text-white" />
                          ) : s.ok ? (
                            <span className="h-1.5 w-1.5 rounded-full bg-[#0A1128]" />
                          ) : (
                            <span className="h-1.5 w-1.5 rounded-full bg-white/40" />
                          )}
                        </span>
                      </div>

                      <div className={`mt-3 text-sm font-semibold leading-snug ${s.active ? "text-white" : s.ok ? "text-white/86" : "text-white/50"}`}>
                        {s.l}
                      </div>
                      <div className={`mt-1 text-[11px] font-mono ${s.active ? "text-[#FFB08F]" : s.ok ? "text-white/55" : "text-white/28"}`}>
                        {s.d}
                      </div>

                      <div className="mt-auto pt-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] ${
                            s.tag === "active"
                              ? "bg-[#FF6B35]/18 text-[#FFB08F]"
                              : s.tag === "done"
                                ? "bg-emerald-400/12 text-emerald-300"
                                : s.tag === "next"
                                  ? "bg-sky-400/12 text-sky-300"
                                  : "bg-white/6 text-white/35"
                          }`}
                        >
                          {s.tag}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="rounded-[1.5rem] bg-[#151D3D] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-white/6 sm:p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-white/45" />
                    <div className="text-sm font-semibold text-white">Required Documents</div>
                  </div>
                  <span className="text-[10px] font-mono text-emerald-300">4 / 5</span>
                </div>
                <div className="space-y-2">
                  {[
                    { l: "Bill of Lading", v: "Complete" },
                    { l: "Commercial Invoice", v: "Complete" },
                    { l: "Packing List", v: "Complete" },
                    { l: "Certificate of Origin", v: "Complete" },
                    { l: "Insurance Cert.", v: "Awaiting", warn: true },
                  ].map((d, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-xl bg-white/[0.04] px-3 py-2.5 ring-1 ring-white/6"
                    >
                      <span className="text-xs text-white/80">{d.l}</span>
                      <span className={`text-[10px] font-semibold ${d.warn ? "text-amber-300" : "text-emerald-300"}`}>
                        {d.v}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[1.5rem] bg-[#151D3D] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-white/6 sm:p-5">
                <div className="mb-4 flex items-center gap-2">
                  <Bell className="h-4 w-4 text-white/45" />
                  <div className="text-sm font-semibold text-white">Real-Time Alerts</div>
                </div>
                <div className="space-y-3">
                  <div className="rounded-xl bg-amber-500/10 px-3 py-3 ring-1 ring-amber-500/20">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                      <span className="text-[10px] font-mono uppercase text-amber-300">ETA shift</span>
                    </div>
                    <div className="text-xs leading-6 text-white/80">
                      SH-2466 ETA shifted by 2 days. New arrival 30 Jun.
                    </div>
                  </div>
                  <div className="rounded-xl bg-white/[0.04] px-3 py-3 ring-1 ring-white/6">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                      <span className="text-[10px] font-mono uppercase text-sky-300">Carrier update</span>
                    </div>
                    <div className="text-xs leading-6 text-white/80">
                      MSC AURIGA passed Suez and remains on schedule.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveFreightDashboard;
